import { Fields } from '@/lib/PlanFields'
import { computed, unref } from 'vue'
import store from '@/store/index'
import { useStorage } from '@vueuse/core'
import useCanViewSalary from '@/hooks/use-can-view-salary'

const conditionalFields = [
  Fields.department,
  Fields.email,
  Fields.location,
  Fields.name,
  Fields.role
]

const defaultFields = [
  Fields.reportingLayers,
  Fields.groupByRole,
  Fields.level,
  Fields.managementCostEtihad,
  Fields.hierarchicalSpanOfControl,
  Fields.directSpanOfControl,
  Fields.vacancies,
  Fields.managerCount,
  Fields.managerCost,
  Fields.icCount,
  Fields.icCost,
  Fields.managerCostRatio,
  Fields.icCostRatio,
  Fields.managerICRatio,
  Fields.totalCost,
  Fields.employeeSkillMatch,
  Fields.agentSkillMatch,
  Fields.dottedLines,
  Fields.rateCard,
  Fields.jobCatalog,
  Fields.personId,
  Fields.payRateEffectiveDate,
  Fields.displayedPositionId,
  Fields.sourceEmployeeId,
  Fields.sourceScenario,
  Fields.directManagerSpanOfControl,
  Fields.managerialSpanOfControl,
  Fields.startDate,
  Fields.endDate
]

// 8 derived/system fields - not toggleable but should show "calculated" badge
// These are system-managed fields derived from other data sources
const derivedFields = [
  Fields.displayedPositionId,
  Fields.dottedLines,
  Fields.groupByRole,
  Fields.jobCatalog,
  Fields.managementCostEtihad,
  Fields.planStatus,
  Fields.rateCard,
  Fields.vacancies
]

// derivedFields set for badge display (calculated fields come from store)
const derivedFieldsSet = new Set(derivedFields)

const scenarioFields = [Fields.plannedHireDate, Fields.planStatus]
const restrictedFields = [Fields.salary]
const restrictedScenarioFields = [Fields.salaryNewRole]
// Derived from compensation, so they follow the same view permission as salary
const costFields = new Set([
  Fields.managementCostEtihad,
  Fields.managerCost,
  Fields.icCost,
  Fields.managerCostRatio,
  Fields.icCostRatio,
  Fields.totalCost
])
const conditionalSelectedFields = [Fields.name, Fields.role, Fields.department, Fields.location]
const defaultSelectedFields = [
  Fields.role,
  Fields.department,
  Fields.location,
  Fields.planStatus,
  Fields.salaryNewRole
]

const selectedFields = useStorage('selected-fields', [])

/**
 * Shared state of selected card fields to be displayed
 */
export default function useScenarioFields(boardId) {
  const mappedFields = computed(() => store.getters.mappedFields(unref(boardId)))
  const isMappedField = (field) => mappedFields.value.includes(field)
  const filteredFieldsByMapping = computed(() =>
    conditionalFields.filter((field) => isMappedField(field))
  )
  const fieldVisibility = computed(() => store.getters.getFieldVisibility || {})
  const calculatedFieldVisibility = computed(() => store.getters.getCalculatedFieldVisibility || {})
  // Toggleable calculated field IDs from store (cached Set for O(1) lookups)
  const calculatedFieldIds = computed(() => store.getters.getCalculatedFieldIds || new Set())
  const calculatedMetricFieldIds = computed(
    () =>
      new Set(
        (store.getters.getCalculatedFields || [])
          .filter((field) => (field.group || 'calculatedMetrics') === 'calculatedMetrics')
          .map((field) => field.id)
      )
  )

  /**
   * Set selected fields.
   * @param {Array} fields - Array of fields to set as selected.
   * @returns {void}
   */
  const setSelectedFields = (fields) => {
    const normalized = Array.isArray(fields) ? [...fields] : []

    // Position title is the fixed scenario card header and should never disappear.
    if (!normalized.includes(Fields.role)) {
      normalized.unshift(Fields.role)
    }

    selectedFields.value = [...new Set(normalized)]
  }

  const enableField = (field) => {
    if (!selectedFields.value.includes(field)) {
      setSelectedFields([...selectedFields.value, field])
    }
  }

  const disableField = (field) => {
    if (selectedFields.value.includes(field)) {
      setSelectedFields(selectedFields.value.filter((f) => f !== field))
    }
  }

  /**
   * Toggles the selected fields based on the provided field.
   * If the field is already selected, it will be removed from the selected fields.
   * If the field is not selected, it will be added to the selected fields.
   * @param {string} field - The field to toggle.
   */
  const toggleField = (field, keepSelected) => {
    if (!keepSelected && selectedFields.value.includes(field)) {
      const newFields = selectedFields.value.filter((f) => f !== field)
      setSelectedFields(newFields)
    } else {
      enableField(field)
    }
  }

  /**
   * Check whether field is currently selected
   */
  const isSelected = (field) => {
    return selectedFields.value.includes(field)
  }

  const isFieldVisible = (fieldId) => {
    const visibilitySettings = fieldVisibility.value || {}
    const hasVisibilitySettings = Object.keys(visibilitySettings).length > 0

    // If no visibility settings exist, show all fields (backward compatibility)
    if (!hasVisibilitySettings) {
      return true
    }

    // Show field if it's not explicitly set to false
    return visibilitySettings[fieldId] !== false
  }

  /**
   * Check if a calculated field is visible based on admin settings
   */
  const isCalculatedFieldVisible = (fieldId) => {
    const visibility = calculatedFieldVisibility.value || {}
    // Default to true (visible) if not explicitly set to false
    return visibility[fieldId] !== false
  }

  /**
   * Reset state to default
   */
  const reset = () => {
    const hasMappedFields = Array.isArray(mappedFields.value) && mappedFields.value.length > 0
    const base = defaultSelectedFields.filter((field) => {
      // Position title is the fixed scenario card header.
      if (field === Fields.role) return true

      if (conditionalSelectedFields.includes(field)) {
        return hasMappedFields ? isMappedField(field) : false
      }

      return true
    })
    setSelectedFields(base)
  }

  const mainBoardId = computed(() => {
    const id = unref(boardId)
    return store.getters.getBoard(id)?.originalBoardId ?? id
  })
  const canViewSalary = useCanViewSalary(mainBoardId)

  const fieldEnabledSet = computed(() => {
    const toggleableIds = calculatedFieldIds.value
    const fields = selectedFields.value || []
    return new Set(
      fields.filter((field) => {
        if (!isFieldVisible(field)) return false
        if (toggleableIds.has(field) && !isCalculatedFieldVisible(field)) return false
        if (costFields.has(field) && !canViewSalary.value) return false
        return true
      })
    )
  })

  // Filter out toggleable calculated fields that are disabled
  const visibleCalculatedFields = computed(() => {
    return [...calculatedFieldIds.value].filter((field) => isCalculatedFieldVisible(field))
  })

  /**
   * Check if a field should show the "calculated" badge
   * Includes both toggleable calculated fields (from store) and derived fields
   */
  const shouldShowCalculatedBadge = (fieldId) => {
    return calculatedMetricFieldIds.value.has(fieldId) || derivedFieldsSet.has(fieldId)
  }

  // Derive calculatedFields array from store for backward compatibility
  const calculatedFields = computed(() => [...calculatedFieldIds.value])

  return {
    calculatedFields,
    calculatedFieldVisibility,
    conditionalFields,
    conditionalSelectedFields,
    costFields,
    defaultFields,
    defaultSelectedFields,
    derivedFields,
    disableField,
    enableField,
    fieldEnabledSet,
    filteredFieldsByMapping,
    isCalculatedFieldVisible,
    isSelected,
    reset,
    restrictedFields,
    restrictedScenarioFields,
    scenarioFields,
    selectedFields,
    setSelectedFields,
    shouldShowCalculatedBadge,
    toggleField,
    visibleCalculatedFields
  }
}
